import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  Optional,
  Output,
  Signal,
  viewChild,
} from '@angular/core';
import { ControlContainer, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { BaseFormDirective } from '../directives/base-form.directive';
import { TsAutocompleteComponent } from '../ts-autocomplete';
import { HTMLInputTypeAttribute } from 'react';
import { MatAutocomplete, MatAutocompleteModule } from '@angular/material/autocomplete';
import { KeyValuePipe, NgClass, NgForOf, NgIf, NgStyle } from '@angular/common';
import { TSIconModule } from '../ts-icon';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';

@Component({
  selector: 'ts-input',
  standalone: true,
  templateUrl: './ts-input.component.html',
  styleUrls: ['./ts-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: TsInputComponent,
      multi: true,
    },
    provideNgxMask(),
  ],
  imports: [
    MatAutocompleteModule,
    ReactiveFormsModule,
    NgStyle,
    TSIconModule,
    NgClass,
    NgIf,
    NgForOf,
    KeyValuePipe,
    NgxMaskDirective,
  ],
})
export class TsInputComponent extends BaseFormDirective implements AfterViewInit {
  @Input() label: string;
  @Input() hint: string;
  @Input() placeholder: string;
  @Input() icon: string;
  @Input() autofocus = false;
  @Input() clearable = false;
  @Input() maxlength: number;
  @Input() min: number;
  @Input() max: number;
  @Input() backgroundColor: 'white' | 'transparent' = 'transparent';
  @Input() mask: string;
  @Input() blurred = false;

  @Input() set type(val: HTMLInputTypeAttribute | 'money') {
    if (val === 'money') {
      this.inputType = 'number';
      this.moneyMin = 0.0;
      this.moneyMax = 10_000_000.0;
      this.step = 0.01;
    }

    this.inputType = val;
  }

  @Input() tabindex = 0;
  @Input() dataCy: string;
  @Input() errorMessages: { [validationErrorKey: string]: string };
  @Input() readonly = false;
  @Input() variant: 'standard' | 'slim' = 'standard';
  @Input() size: 'md' | 'lg' | 'sm' | 'ss' = 'md';
  @Input() autocomplete = 'off';

  @Output() blur = new EventEmitter<void>();

  @ContentChild(TsAutocompleteComponent) autoCompleteComponentRef: TsAutocompleteComponent;
  inputElement: Signal<ElementRef<HTMLInputElement> | undefined> = viewChild('inputElement');
  matAutocompleteComponentRef: MatAutocomplete;

  inputType = 'text';
  moneyMin: number;
  moneyMax: number;
  step: number;

  constructor(@Optional() protected controlContainer: ControlContainer) {
    super(controlContainer);
  }

  ngAfterViewInit(): void {
    if (this.autoCompleteComponentRef) {
      this.matAutocompleteComponentRef = this.autoCompleteComponentRef.autocomplete;
    }

    this.focusInputIfNeeded();
  }

  onInputBlur(): void {
    this.blur.emit();
    this.notifyStandaloneTouched();
  }

  private focusInputIfNeeded(): void {
    if (!this.autoFocusEnabled()) {
      return;
    }

    setTimeout(() => this.inputElement()?.nativeElement.focus(), 0);
  }

  private autoFocusEnabled(): boolean {
    return this.autofocus && !this.readonly;
  }
}
